import React, { useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';

const RemarkModal = ({ isOpen, onClose, onSubmit, record, loading }) => {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!remark.trim()) {
      setError('Please enter a remark');
      return;
    }
    if (remark.trim().length < 5) {
      setError('Remark must be at least 5 characters');
      return;
    }
    onSubmit(remark);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 bg-black/50 dark:bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md transition-all duration-300 transform bg-white border border-gray-100 shadow-2xl dark:bg-gray-900 rounded-2xl animate-in fade-in zoom-in dark:border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-800 dark:text-white">
            <div className="p-1.5 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
              <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            Add Remark
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition-all duration-200 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:rotate-90"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Record Info */}
          <div className="p-4 space-y-2 transition-all duration-300 border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800/30 rounded-xl dark:border-gray-700">
            <p className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Date:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {record?.DateString ? new Date(record.DateString).toLocaleDateString() : 'N/A'}
              </span>
            </p>
            <p className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Status:</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                record?.status === 'P' 
                  ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' 
                  : record?.status === 'A' 
                    ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>
                {record?.status === 'P' ? 'Present' : record?.status === 'A' ? 'Absent' : 'N/A'}
              </span>
            </p>
            {record?.IN_Time && (
              <p className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Check In:</span>
                <span className="font-mono text-gray-900 dark:text-white">{record.IN_Time}</span>
              </p>
            )}
            {record?.OUT_Time && (
              <p className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Check Out:</span>
                <span className="font-mono text-gray-900 dark:text-white">{record.OUT_Time}</span>
              </p>
            )}
          </div>

          {/* Remark Input */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Your Remark <span className="text-blue-500 dark:text-blue-400">*</span>
            </label>
            <textarea
              value={remark}
              onChange={(e) => {
                setRemark(e.target.value);
                if (error) setError('');
              }}
              rows="4"
              placeholder="Please enter your remark about this attendance record..."
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none ${
                error 
                  ? 'border-red-500 dark:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-400'
              } bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
            />
            {error && (
              <p className="flex items-center gap-1 mt-2 text-xs text-red-500 dark:text-red-400">
                <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {error}
              </p>
            )}
          </div>

          {/* Character Counter */}
          <div className="text-right">
            <span className={`text-xs transition-colors duration-200 ${
              remark.length > 0 
                ? 'text-bluee-600 dark:text-blue-400 font-medium' 
                : 'text-gray-400 dark:text-gray-500'
            }`}>
              {remark.length} characters {remark.length > 0 && '• Minimum 5 characters'}
            </span>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white px-4 py-2.5 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-500/25"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Add Remark
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RemarkModal;